import { Vector3 } from "@babylonjs/core";
import { JUNGLE_CONFIG, type JungleObstacle } from "../environment/JungleGenerator";
import type { ActiveGhostController, PontianakAggressionEvent } from "../ghosts/GhostController";
import { ACTIVE_GHOST_IDS, type ImplementedGhostId } from "../ghosts/GhostDefinitions";
import { GhostState } from "../ghosts/GhostStateMachine";
import type { ToyolController } from "../ghosts/ToyolController";
import type { PlacedBait } from "./LureSystem";
import type { NavigationGrid } from "./NavigationGrid";

export interface SpawnConfig {
  readonly minSpawnDistance: number;
  readonly maxSpawnDistance: number;
  readonly respawnDelayMin: number;
  readonly respawnDelayMax: number;
}

export const DEFAULT_SPAWN_CONFIG: SpawnConfig = {
  minSpawnDistance: JUNGLE_CONFIG.ghostSpawnDistance.min,
  maxSpawnDistance: JUNGLE_CONFIG.ghostSpawnDistance.max,
  respawnDelayMin: 8,
  respawnDelayMax: 18,
};

export const MIN_ACTIVE_GHOST_SEPARATION = 1.25;

/**
 * Applies a tiny navigation-safe crowd correction after normal controller
 * movement. With a hard cap of three ghosts this is at most three pair checks,
 * and avoids pathfinding or a second movement loop in each controller.
 */
export function separateActiveGhosts(
  ghosts: readonly ActiveGhostController[],
  navigation: NavigationGrid,
  obstacles: readonly JungleObstacle[],
  minimumSeparation = MIN_ACTIVE_GHOST_SEPARATION,
): void {
  const movable = ghosts.filter((ghost) => ghost.diagnosticsPositionFrozen!==true&&![GhostState.CAPTURING, GhostState.CAPTURED].includes(ghost.state));
  const isSafe = (position: Vector3) => navigation.isWalkableWorld(position)
    && !obstacles.some((obstacle) => Vector3.DistanceSquared(position, obstacle.position) <= (obstacle.radius + 0.42) ** 2);

  for (let firstIndex = 0; firstIndex < movable.length; firstIndex++) {
    const first = movable[firstIndex];
    if (!first) continue;
    for (let secondIndex = firstIndex + 1; secondIndex < movable.length; secondIndex++) {
      const second = movable[secondIndex];
      if (!second) continue;
      const offset = second.position.subtract(first.position);
      offset.y = 0;
      const distance = offset.length();
      if (distance >= minimumSeparation) continue;
      const direction = distance > 0.001 ? offset.scale(1 / distance) : new Vector3(1, 0, 0);
      const correction = direction.scale((minimumSeparation - distance) * 0.5);
      const firstCandidate = first.position.subtract(correction);
      const secondCandidate = second.position.add(correction);
      const firstSafe = isSafe(firstCandidate);
      const secondSafe = isSafe(secondCandidate);
      if (firstSafe) first.position.copyFrom(firstCandidate);
      if (secondSafe) second.position.copyFrom(secondCandidate);
      if (firstSafe !== secondSafe) {
        const remaining = direction.scale((minimumSeparation - Vector3.Distance(first.position, second.position)) * (firstSafe ? 1 : -1));
        const candidate = (firstSafe ? first.position : second.position).subtract(remaining);
        if (isSafe(candidate)) (firstSafe ? first.position : second.position).copyFrom(candidate);
      }
    }
  }
}

export function selectSpawnPoint(
  candidates: readonly Vector3[],
  playerPosition: Vector3,
  cameraForward: Vector3,
  obstacles: readonly JungleObstacle[],
  navigation: NavigationGrid,
  config: SpawnConfig = DEFAULT_SPAWN_CONFIG,
  occupied: readonly Vector3[] = [],
  minimumGhostSeparation = 3.2,
  excluded: readonly Vector3[] = [],
  minimumExcludedSeparation = 1.1,
): Vector3 | undefined {
  const horizontalForward = new Vector3(cameraForward.x, 0, cameraForward.z).normalize();
  const valid = candidates.filter((candidate) => {
    const distance = Vector3.Distance(candidate, playerPosition);
    if (distance < config.minSpawnDistance || distance > config.maxSpawnDistance || !navigation.isWalkableWorld(candidate)) return false;
    if (occupied.some((position) => Vector3.DistanceSquared(candidate, position) < minimumGhostSeparation ** 2)) return false;
    if (excluded.some((position) => Vector3.DistanceSquared(candidate, position) < minimumExcludedSeparation ** 2)) return false;
    if (obstacles.some((obstacle) => Vector3.DistanceSquared(candidate, obstacle.position) <= (obstacle.radius + 0.45) ** 2)) return false;
    return navigation.findPath(playerPosition, candidate).length > 0;
  });
  if (valid.length === 0) return undefined;
  valid.sort((a, b) => {
    const directionA = a.subtract(playerPosition).normalize();
    const directionB = b.subtract(playerPosition).normalize();
    return Vector3.Dot(horizontalForward, directionA) - Vector3.Dot(horizontalForward, directionB);
  });
  return valid[0]?.clone();
}

export type GhostFactory = (spawn: Vector3, runtimeId: string, type: ImplementedGhostId) => ActiveGhostController;
type GhostFactories = Readonly<Partial<Record<ImplementedGhostId, GhostFactory>>>;

export class GhostSpawnManager {
  private readonly active = new Map<ImplementedGhostId, ActiveGhostController>();
  private readonly respawnCountdown = new Map<ImplementedGhostId, number>();
  private readonly removalCountdown = new Map<ImplementedGhostId, number>();
  private disposed = false;
  private sequence = 0;
  private diagnosticsEnabled=false;
  private readonly factories: Partial<GhostFactories>;
  private readonly spawnTypes: readonly ImplementedGhostId[];

  constructor(
    private readonly waypoints: readonly Vector3[],
    private readonly obstacles: readonly JungleObstacle[],
    private readonly navigation: NavigationGrid,
    factoryOrFactories: ((spawn: Vector3) => ToyolController) | GhostFactories,
    private readonly config: SpawnConfig = DEFAULT_SPAWN_CONFIG,
    private readonly random: () => number = Math.random,
    private readonly campaignMode = false,
  ) {
    if (typeof factoryOrFactories === "function") {
      this.spawnTypes = ["toyol"];
      this.factories = { toyol: (spawn) => factoryOrFactories(spawn) };
    } else {
      this.spawnTypes = campaignMode?ACTIVE_GHOST_IDS:["toyol","polong","pontianak"];
      this.factories = factoryOrFactories;
    }
  }

  get activeGhost(): ToyolController | undefined { return this.active.get("toyol") as ToyolController | undefined; }
  get activeGhosts(): readonly ActiveGhostController[] { return [...this.active.values()]; }
  get activeCount(): number { return this.active.size; }
  get hasRespawnScheduled(): boolean { return this.respawnCountdown.size > 0; }

  start(playerPosition: Vector3, cameraForward: Vector3): boolean {
    if (this.disposed || this.active.size > 0) return false;
    let spawned = false;
    for (const type of this.spawnTypes) spawned = this.spawn(type, playerPosition, cameraForward) || spawned;
    return spawned;
  }
  startType(type:ImplementedGhostId,playerPosition:Vector3,cameraForward:Vector3,lures:readonly PlacedBait[]=[]):boolean{if(this.campaignMode&&this.active.size>0)return false;return this.spawn(type,playerPosition,cameraForward,lures.map((lure)=>lure.position));}
  clearActive():void{for(const ghost of this.active.values())ghost.dispose();this.active.clear();this.removalCountdown.clear();this.respawnCountdown.clear();}

  removeCaptured(runtimeId:string):boolean{
    const entry=[...this.active].find(([,ghost])=>ghost.runtimeId===runtimeId);
    if(!entry||entry[1].state!==GhostState.CAPTURED)return false;
    entry[1].dispose();this.active.delete(entry[0]);this.removalCountdown.delete(entry[0]);this.respawnCountdown.delete(entry[0]);return true;
  }

  update(deltaSeconds: number, playerPosition: Vector3, cameraForward: Vector3, lures: readonly PlacedBait[]): void {
    if (this.disposed) return;
    const frameStarts=new Map<string,Vector3>();for(const ghost of this.activeGhosts)if(ghost.position)frameStarts.set(ghost.runtimeId,ghost.position.clone());
    for (const [type, ghost] of [...this.active]) {
      ghost.update(deltaSeconds, lures, playerPosition);
      const removal = this.removalCountdown.get(type);
      if (removal === undefined) continue;
      const next = removal - deltaSeconds;
      if (next > 0) { this.removalCountdown.set(type, next); continue; }
      ghost.dispose(); this.active.delete(type); this.removalCountdown.delete(type);
      if(!this.campaignMode)this.respawnCountdown.set(type, this.config.respawnDelayMin + this.random() * (this.config.respawnDelayMax - this.config.respawnDelayMin));
    }
    separateActiveGhosts(this.activeGhosts, this.navigation, this.obstacles);
    for(const ghost of this.active.values()){const previous=frameStarts.get(ghost.runtimeId);if(previous)ghost.finalizeWorldMovement?.(previous,deltaSeconds);}
    for (const [type, remaining] of [...this.respawnCountdown]) {
      const next = remaining - deltaSeconds;
      if (next > 0) { this.respawnCountdown.set(type, next); continue; }
      if (this.spawn(type, playerPosition, cameraForward)) this.respawnCountdown.delete(type);
      else this.respawnCountdown.set(type, 2);
    }
  }

  markCaptured(runtimeIdOrDelay?: string | number, removalDelaySeconds = 0.15): void {
    const runtimeId = typeof runtimeIdOrDelay === "string" ? runtimeIdOrDelay : undefined;
    const delay = typeof runtimeIdOrDelay === "number" ? runtimeIdOrDelay : removalDelaySeconds;
    const entry = runtimeId ? [...this.active].find(([, ghost]) => ghost.runtimeId === runtimeId) : [...this.active][0];
    if (!entry || this.removalCountdown.has(entry[0])) return;
    this.removalCountdown.set(entry[0], Math.max(0, delay));
  }

  forgetLure(id: number): void { for (const ghost of this.active.values()) ghost.forgetLure(id); }
  clearAllBaitTargets(): void { for (const ghost of this.active.values()) ghost.onAllBaitCleared(); }
  broadcastPontianakAggression(event:PontianakAggressionEvent):void{for(const ghost of this.active.values())if(ghost.runtimeId!==event.pontianakInstanceId)ghost.onPontianakAggression?.(event);}
  setDiagnosticsEnabled(enabled:boolean,playerPosition:Vector3,cameraForward:Vector3):void{this.diagnosticsEnabled=enabled;const aimDirection=cameraForward.lengthSquared()>.001?cameraForward.normalizeToNew():Vector3.Forward();const clear=(point:Vector3)=>this.navigation.isWalkableWorld(point)&&!this.obstacles.some((obstacle)=>Math.hypot(point.x-obstacle.position.x,point.z-obstacle.position.z)<obstacle.radius+.5);const controlled=[4,6,8,10,12].map((distance)=>playerPosition.add(aimDirection.scale(distance))).find(clear);const right=new Vector3(aimDirection.z,0,-aimDirection.x);const pontianakPosition=[2.4,-2.4,3.2,-3.2].map((offset)=>playerPosition.add(aimDirection.scale(5)).add(right.scale(offset))).map((point)=>new Vector3(point.x,0,point.z)).find(clear);for(const ghost of this.active.values())ghost.setDiagnosticsEnabled?.(enabled,ghost.ghostId==="polong"?controlled:ghost.ghostId==="pontianak"?pontianakPosition:undefined);}

  dispose(): void {
    this.disposed = true; this.respawnCountdown.clear(); this.removalCountdown.clear();
    for (const ghost of this.active.values()) ghost.dispose(); this.active.clear();
  }

  private spawn(type: ImplementedGhostId, playerPosition: Vector3, cameraForward: Vector3, excluded:readonly Vector3[]=[]): boolean {
    if (this.active.has(type) || this.active.size >= (this.campaignMode?1:3) || this.disposed) return false;
    const factory = this.factories[type]; if (!factory) return false;
    const candidates = [...this.waypoints];
    for (let index = 0; index < 24; index++) candidates.push(this.navigation.randomWalkable(this.random));
    const spawn = selectSpawnPoint(candidates, playerPosition, cameraForward, this.obstacles, this.navigation, this.config, this.activeGhosts.map((ghost) => ghost.position),3.2,excluded);
    if (!spawn) return false;
    const runtimeId = `${type}-${++this.sequence}`;
    const ghost=factory(spawn,runtimeId,type);this.active.set(type,ghost);if(this.diagnosticsEnabled)ghost.setDiagnosticsEnabled?.(true);
    return true;
  }
}
